// src/contexts/ModalContext.tsx

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface ModalContextType {
  isDayCloseModalOpen: boolean;
  setIsDayCloseModalOpen: (isOpen: boolean) => void;

  // 暫停/恢復機制
  isDayCloseModalPaused: boolean;
  pauseDayCloseModal: () => void;
  resumeDayCloseModal: () => void;
}

// 創建 Context
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// 提供 Context 的 Provider 元件
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDayCloseModalOpen, setIsDayCloseModalOpen] = useState(false);
  const [isDayCloseModalPaused, setIsDayCloseModalPaused] = useState(false);

  // 暫停 Day Close Modal（隱藏 UI 但保持狀態）
  const pauseDayCloseModal = useCallback(() => {
    setIsDayCloseModalPaused(true);
  }, []);

  // 恢復 Day Close Modal
  const resumeDayCloseModal = useCallback(() => {
    setIsDayCloseModalPaused(false);
  }, []);

  // Memoize value 對象以避免不必要的重新渲染
  const value = useMemo(() => ({
    isDayCloseModalOpen,
    setIsDayCloseModalOpen,
    isDayCloseModalPaused,
    pauseDayCloseModal,
    resumeDayCloseModal
  }), [isDayCloseModalOpen, isDayCloseModalPaused, pauseDayCloseModal, resumeDayCloseModal]);

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

// 自定義 Hook，方便使用 Context
export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};